import DataTable from '@/components/ui/DataTable';
import { useStyledTable } from '@/hooks/useStyledTable';
import {
  AGENCY_SELECTION_COLUMNS,
  SharedAgency,
} from '@/types/distributors/agencySelectionColumnDef';
import { RowSelectionState, SortingState } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import DistributorInfoModal from './DistributorInfoModal';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';

interface AgencySelectionTableProps {
  agencies: SharedAgency[];
  setSelectedAgencyRows: React.Dispatch<
    React.SetStateAction<RowSelectionState>
  >;
  shouldSelectAll: boolean;
  selectedNetworks: string[];
  sorting: SortingState;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
}

const AgencySelectionTable = ({
  agencies,
  setSelectedAgencyRows,
  shouldSelectAll = false,
  selectedNetworks,
  sorting,
  setSorting,
}: AgencySelectionTableProps) => {
  'use no memo';

  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const defaultRowSelection = useMemo(
    () =>
      agencies
        .filter((agency) => agency.allCircuitsAreShared)
        .reduce((acc, { id }) => ({ ...acc, [id]: true }), {}),
    [agencies],
  );

  const selectedNetworksAgenciesSelection = useMemo(
    () =>
      agencies
        .filter(
          (agency) =>
            agency.network && selectedNetworks.includes(agency.network.id),
        )
        .map((agency) => agency.id)
        .reduce((acc, curr) => ({ ...acc, [curr]: true }), {}),
    [agencies, selectedNetworks],
  );

  const [, setRowSelection] = useState<RowSelectionState>(defaultRowSelection);
  const [distributorToViewId, setDistributorToViewId] = useState<string | null>(
    null,
  );

  const table = useStyledTable({
    columns: getTranslatedConst(AGENCY_SELECTION_COLUMNS),
    data: agencies,
    enablePagination: false,
    setSelection: (selection) => {
      setRowSelection(selection);
      setSelectedAgencyRows(selection);
    },
    getRowId: (row) => row.id,
    defaultRowSelection,
    sorting,
    setSorting,
    onViewRow: (row) => {
      setDistributorToViewId(row.original.id);
    },
  });

  useEffect(() => {
    if (shouldSelectAll) table.toggleAllRowsSelected(true);
    else {
      if (selectedNetworks.length > 0) {
        table.setRowSelection(selectedNetworksAgenciesSelection);
      } else table.setRowSelection(defaultRowSelection);
    }
  }, [
    defaultRowSelection,
    selectedNetworks.length,
    selectedNetworksAgenciesSelection,
    shouldSelectAll,
    table,
  ]);

  return (
    <>
      <div className="flex flex-col gap-2">
        <p className="text-base font-medium text-black">
          {t('modal.selectAgencies.yourDistributors')} ({agencies.length})
        </p>
        <div className="max-h-[492px] w-full overflow-auto">
          <DataTable table={table} sorting={sorting} />
        </div>
      </div>
      {distributorToViewId && (
        <DistributorInfoModal
          distributorId={distributorToViewId}
          closeModal={() => setDistributorToViewId(null)}
        />
      )}
    </>
  );
};

export default AgencySelectionTable;
